import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ModalPortal, Text } from "@livechat/design-system-react-components";

import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { prepareMailboxAddress } from "helpers/mailboxes";
import { selectAgents } from "store/entities/agents";
import { selectLicenseId } from "store/entities/license";
import { IWithMailboxesState, selectIsDeletingMailbox, selectMailbox } from "store/entities/mailboxes/selectors";
import { selectTeams } from "store/entities/teams";
import { SettingsViewActions } from "store/views/settings";
import { StackingContextLevel } from "styles/stacking-context-level";

interface IDeleteInboxModalProps {
  inboxId: string;
  onClose: () => void;
}

const DeleteInboxModal: FC<IDeleteInboxModalProps> = ({ inboxId, onClose }) => {
  const dispatch = useDispatch();

  const licenseId = useSelector(selectLicenseId);
  const teams = useSelector(selectTeams);
  const agents = useSelector(selectAgents);
  const inbox = useSelector(state => selectMailbox(state as IWithMailboxesState, inboxId));

  const isDeleting = useSelector(selectIsDeletingMailbox);
  const [prevIsDeleting, setPrevIsDeleting] = useState(isDeleting);

  useEffect(() => {
    if (prevIsDeleting && !isDeleting) {
      onClose();
      return;
    }
    setPrevIsDeleting(isDeleting);
  }, [isDeleting, prevIsDeleting, onClose]);

  if (!inbox) {
    return null;
  }

  const { name, teamId, agentId } = inbox;
  const team = teams[teamId];
  const agent = agentId ? agents[agentId] : undefined;
  const forwardingAddress = prepareMailboxAddress(licenseId, name);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onDelete = () => {
    dispatch(SettingsViewActions.deleteMailbox({ id: inboxId }));
  };
  return (
    <ModalPortal zIndex={StackingContextLevel.Modal}>
      <ConfirmActionModal
        heading="Delete forwarding address?"
        onClose={onClose}
        primaryAction={{
          label: "Delete",
          dataTestId: "delete-forwarding-address-button",
          onClick: onDelete,
          loading: isDeleting,
          actionType: "destructive",
        }}
        secondaryAction={{
          label: "Cancel",
          onClick: onClose,
        }}
      >
        <Text>You&apos;re about to delete the following forwarding address:</Text>
        <Text bold>{forwardingAddress}</Text>
        <br />
        <Text>Assigned to:</Text>
        <Text bold>{team.name}</Text>
        <Text bold>{agent?.name}</Text>
        <br />
        <Text>Deleting the forwarding address is irreversible.</Text>
      </ConfirmActionModal>
    </ModalPortal>
  );
};

export default DeleteInboxModal;
